import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { backupQuery, post, profileQuery, requestDeviceTree, taskQuery, type ProtecteeBackup } from '../api/protectee'
import { ApiError, type PostValue } from '../api/client'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { AlgorithmForm, AlgorithmView, Tree, TreeForm, collect, formFields } from './backupForms'

/**
 * Fila Backup settings - `Protectee::deviceBackupSettings` (backup/settings.php):
 * comutatorul Tasks / Profiles, lista lor ca file (`tasksList.php`,
 * `profilesList.php`), "Create task" (cei patru pasi din `newTaskModal.php`:
 * nume, algoritm, destinatie, arbore) si "Assign profile"
 * (`assignProfileModal.php`). O sarcina deschisa arata `taskDetails.php`, pe
 * doua coloane: Settings si Directory Tree in stanga, Algorithm si Actions
 * (Save as profile, Delete task) in dreapta. Un profil e numai de citit aici
 * (`profileDetails.php`) - se administreaza din Settings > Protectee.
 *
 * Formularele de algoritm si de arbore sunt in `backupForms.tsx`, comune cu
 * Settings > Protectee.
 */

/** Destinatia Azure (`it_protectee_backup_destinations.ID = 3`): cheie si cont in loc de cale, utilizator si parola. */
export const AZURE = 3

async function send(path: string, fields: Record<string, PostValue>, onDone?: (answer: { task?: number; profile?: number }) => void): Promise<boolean> {
  try {
    const body = await post(path, fields)
    toast(body.message, body.status === 'OK' ? 'Success' : 'Error', body.status === 'OK' ? 'success' : 'error')
    if (body.status === 'OK') onDone?.(body)
    return body.status === 'OK'
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    return false
  }
}

export function BackupTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(backupQuery(id))
  const [kind, setKind] = useState<'tasks' | 'profiles'>('tasks')
  const [openTask, setOpenTask] = useState<number | null>(null)
  const [openProfile, setOpenProfile] = useState<number | null>(null)
  const [wizard, setWizard] = useState(false)
  const [assign, setAssign] = useState(false)
  const refresh = () => void client.invalidateQueries({ queryKey: ['protectee', 'device', id] })
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const b = q.data

  return (
    <div className="protectee-backup">
      <div className="row">
        <div className="col-md-12">
          <div className="btn-group" data-toggle="buttons">
            <label className={`btn btn-primary btn-transparent backup-type${kind === 'tasks' ? ' active' : ''}`} onClick={() => setKind('tasks')}>
              <input type="radio" name="backupType" value="0" checked={kind === 'tasks'} readOnly /> Tasks
            </label>
            <label className={`btn btn-primary btn-transparent backup-type${kind === 'profiles' ? ' active' : ''}`} onClick={() => setKind('profiles')}>
              <input type="radio" name="backupType" value="1" checked={kind === 'profiles'} readOnly /> Profiles
            </label>
          </div>
          <button className="btn btn-primary btn-sm pull-right task-form" style={{ marginLeft: 10 }} type="button" onClick={() => setWizard(true)}>
            <i className="fas fa-plus-circle"></i> Create task
          </button>
          <button className="btn btn-success btn-sm pull-right task-form" style={{ marginLeft: 10 }} type="button" onClick={() => setAssign(true)}>
            <i className="fas fa-plus-circle"></i> Assign profile
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 settings-content" style={{ marginTop: 10 }}>
          {kind === 'tasks' ? (
            b.tasks.length === 0 ? <div className="alert alert-info"><strong>No tasks defined</strong></div> : (
              <ul className="nav nav-tabs tasksList" role="tablist">
                {b.tasks.map((t) => (
                  <li key={t.ID} className={openTask === t.ID ? 'active' : undefined}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setOpenTask(t.ID); setOpenProfile(null) }}><i className="fa fa-calendar"></i> {t.name}</a>
                  </li>
                ))}
              </ul>
            )
          ) : b.device_profiles.length === 0 ? <div className="alert alert-info"><strong>No profiles assigned</strong></div> : (
            <ul className="nav nav-tabs profilesList" role="tablist">
              {b.device_profiles.map((p) => (
                <li key={p.profile_id} className={openProfile === p.profile_id ? 'active' : undefined}>
                  <a href="#" onClick={(e) => { e.preventDefault(); setOpenProfile(p.profile_id); setOpenTask(null) }}><i className="fa fa-calendar"></i> {p.name}</a>
                </li>
              ))}
            </ul>
          )}
          {kind === 'tasks' && openTask !== null ? <TaskPanel key={openTask} task={openTask} backup={b} onChanged={() => { refresh(); void client.invalidateQueries({ queryKey: ['protectee', 'task', openTask] }) }} onGone={() => { setOpenTask(null); refresh() }} /> : null}
          {kind === 'profiles' && openProfile !== null ? <ProfilePanel key={openProfile} profile={openProfile} device={id} backup={b} onRemoved={() => { setOpenProfile(null); refresh() }} /> : null}
        </div>
      </div>
      {wizard ? <NewTaskWizard device={id} backup={b} onClose={() => { setWizard(false); refresh() }} /> : null}
      <AssignProfile device={id} backup={b} open={assign} onClose={() => setAssign(false)} onSaved={() => { setAssign(false); refresh() }} />
    </div>
  )
}

/* ------------------------------------------------------------- destinatie */

type Destination = { destination_id: number; path: string; user: string; has_password: boolean; destination_key: string; name?: string }

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-group">
      <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">{label}</span></label>
      <div className="col-sm-6">{children}</div>
    </div>
  )
}

/**
 * Setarile unei sarcini. `taskSettingsForm.php` (pasul 3): destinatia se
 * alege, iar Azure schimba Path / User / Password pe Azure key / Azure account.
 *
 * Pe o sarcina EXISTENTA (`taskDetails.php`) numele se schimba, si - spre
 * deosebire de portal, unde destinatia era o eticheta - se schimba si
 * destinatia (decizia Roxanei, 18 septembrie 2026). Cat timp destinatia e cea
 * salvata, campurile ei vin completate; cand se alege alta, campurile noii
 * destinatii pornesc goale, ca nicio cale, cont sau parola a celei vechi sa nu
 * treaca pe noua din greseala.
 */
function SettingsForm({ backup, values, onSubmit, submitLabel, existing }: {
  backup: ProtecteeBackup
  values: Destination
  onSubmit(fields: Record<string, PostValue>): void
  submitLabel: string
  existing?: boolean
}) {
  const saved = String(values.destination_id || '')
  const [destination, setDestination] = useState(saved || String(backup.destinations[0]?.ID ?? ''))
  const azure = Number(destination) === AZURE
  const same = existing === true && destination === saved
  const shown: Destination = same ? values : { destination_id: Number(destination), path: '', user: '', has_password: false, destination_key: '' }
  return (
    <form className="form-horizontal labelcustomize tk-action-form taskSettings" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); onSubmit(formFields(e.currentTarget)) }}>
      {existing ? <Field label="Name"><input type="text" className="form-control" name="taskName" defaultValue={values.name ?? ''} required /></Field> : null}
      <Field label="Destination">
        <select className="form-control" name="destination" value={destination} onChange={(e) => setDestination(e.target.value)} required>
          {existing && saved !== '' && !backup.destinations.some((d) => String(d.ID) === saved) ? <option value={saved} disabled>Select...</option> : null}
          {backup.destinations.map((d) => <option key={d.ID} value={d.ID}>{d.name}</option>)}
        </select>
      </Field>
      {/* `key`: la alta destinatie campurile se refac de la zero, nu pastreaza ce era scris. */}
      <div key={destination}>
        {azure ? (
          <>
            <Field label="Azure key"><input type="text" className="form-control" name="azureKey" defaultValue={shown.destination_key} required /></Field>
            <Field label="Azure account"><input type="text" className="form-control" name="user" defaultValue={shown.user} required={!same} /></Field>
          </>
        ) : (
          <>
            <Field label="Path"><input type="text" className="form-control" name="path" defaultValue={shown.path} required /></Field>
            <Field label="User"><input type="text" className="form-control" name="user" defaultValue={shown.user} /></Field>
            <Field label="Password"><input type="password" className="form-control" name="password" defaultValue={shown.has_password ? '********' : ''} autoComplete="new-password" /></Field>
          </>
        )}
      </div>
      <button type="submit" className="btn btn-sm btn-primary">{submitLabel}</button>
    </form>
  )
}

/* ------------------------------------------------------------- sarcina */

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="panel panel-primary">
      <div className="panel-heading">{title}</div>
      <div className="panel-body">{children}</div>
    </div>
  )
}

/** `taskDetails.php` */
function TaskPanel({ task, backup, onChanged, onGone }: { task: number; backup: ProtecteeBackup; onChanged(): void; onGone(): void }) {
  const q = useQuery(taskQuery(task))
  const [saveAs, setSaveAs] = useState(false)
  const [profileName, setProfileName] = useState('')
  // O fila ramasa in urma unei sarcini deja sterse (ex. dupa Cancel): 404 = nu
  // mai exista, deci scoate fila si reimprospateaza lista in loc sa arate
  // eroarea rosie "Not found in the selected company".
  const gone = q.error instanceof ApiError && q.error.status === 404
  useEffect(() => { if (gone) onGone() }, [gone, onGone])
  if (gone) return null
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const d = q.data
  const createProfile = () => {
    if (profileName.trim() === '') return
    void send(`/protectee/tasks/${task}/profile`, { profileName }, () => { setProfileName(''); setSaveAs(false); onChanged() })
  }
  return (
    <div className="row task-details" style={{ marginTop: 10 }}>
      <div className="col-md-6">
        <Panel title="Settings">
          <SettingsForm backup={backup} values={{ ...d.task }} existing submitLabel="Update" onSubmit={(f) => void send(`/protectee/tasks/${task}/settings?change=1`, f, onChanged)} />
        </Panel>
        <Panel title="Directory Tree">
          <TreeForm tree={d.tree} submitLabel="Update" onSubmit={(paths) => void send(`/protectee/tasks/${task}/tree`, { directory: paths }, onChanged)} />
        </Panel>
      </div>
      <div className="col-md-6">
        <Panel title="Algorithm">
          <AlgorithmForm backup={backup} algorithm={d.algorithm} submitLabel="Update" onSubmit={(f) => void send(`/protectee/tasks/${task}/algorithm?change=1`, f, onChanged)} />
        </Panel>
        <Panel title="Actions">
          <button type="button" className="btn btn-success btn-sm runNow" onClick={() => { if (confirm('Run this backup now?')) void send(`/protectee/tasks/${task}/run`, {}, onChanged) }}>
            <i className="fa fa-play"></i> Run Now
          </button>{' '}
          <button type="button" className="btn btn-primary btn-sm saveAsProfile" onClick={() => setSaveAs(true)}>
            <i className="fa fa-save"></i> Save as profile
          </button>{' '}
          <button type="button" className="btn btn-danger btn-sm deleteTask" onClick={() => { if (confirm('Are you sure you want to delete task?')) void send(`/protectee/tasks/${task}/delete`, {}, onGone) }}>
            <i className="fa fa-trash"></i> Delete task
          </button>
        </Panel>
      </div>
      {/* createProfileModal.php */}
      <Modal
        id={`createProfileForm-${task}`}
        open={saveAs}
        onClose={() => setSaveAs(false)}
        title="Save as Profile"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setSaveAs(false)}>Close</button>
            <button type="button" className="btn btn-primary saveProfile" onClick={createProfile}>Save</button>
          </>
        }
      >
        <form className="form-horizontal labelcustomize createProfile" onSubmit={(e) => { e.preventDefault(); createProfile() }}>
          <Field label="Name"><input type="text" className="form-control" name="profileName" value={profileName} onChange={(e) => setProfileName(e.target.value)} required /></Field>
        </form>
      </Modal>
    </div>
  )
}

/** `profileDetails.php`: numai citire. */
function ProfilePanel({ profile, device, backup, onRemoved }: { profile: number; device: number; backup: ProtecteeBackup; onRemoved(): void }) {
  const q = useQuery(profileQuery(profile, device))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const d = q.data
  const dest = backup.destinations.find((x) => x.ID === d.profile.destination_id)
  return (
    <div className="row profile-details" style={{ marginTop: 10 }}>
      <div className="col-md-6">
        <Panel title="Settings">
          <table className="details-table" style={{ width: '100%' }}>
            <tbody>
              <tr><td className="title">Name:</td><td>{d.profile.name}</td></tr>
              <tr><td className="title">Destination:</td><td>{dest?.name ?? ''}</td></tr>
              {d.profile.destination_id === AZURE ? <tr><td className="title">Azure key:</td><td>{d.profile.destination_key}</td></tr> : null}
              <tr><td className="title">Path:</td><td>{d.profile.path}</td></tr>
              <tr><td className="title">User:</td><td>{d.profile.user}</td></tr>
            </tbody>
          </table>
        </Panel>
        <Panel title="Directory Tree">
          <Tree nodes={d.tree} checked={collect(d.tree, new Set())} onToggle={() => undefined} disabled />
        </Panel>
      </div>
      <div className="col-md-6">
        <Panel title="Algorithm">
          <AlgorithmView algorithm={d.algorithm} lists={backup} />
        </Panel>
        <Panel title="Actions">
          <button type="button" className="btn btn-danger btn-sm removeProfile" onClick={() => { if (confirm('Are you sure you want to remove this profile from the device?')) void send(`/protectee/profiles/${profile}/remove`, { device }, onRemoved) }}>
            <i className="fa fa-trash"></i> Remove profile
          </button>
        </Panel>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- modale */

/**
 * newTaskModal.php: pasii 1-4 (taskNameForm, taskAlgorithmForm, taskSettingsForm,
 * taskDirectoryTreeForm). Fereastra nu se inchide la clic pe fundal
 * (`data-backdrop="static"`); Cancel dupa pasul 1 sterge sarcina inceputa
 * (cancelNewTask).
 */
function NewTaskWizard({ device, backup, onClose }: { device: number; backup: ProtecteeBackup; onClose(): void }) {
  const [step, setStep] = useState(1)
  const [task, setTask] = useState<number | null>(null)
  const [name, setName] = useState('')
  const tree = useQuery({
    ...taskQuery(task ?? 0),
    enabled: task !== null && step === 4,
    // Cat timp arborele e gol, reintreaba - agentul il trimite la urmatorul check-in.
    refetchInterval: (q) => (q.state.data && q.state.data.tree.length === 0 ? 4000 : false),
  })
  // La deschiderea pasului 4, cere agentului un arbore proaspat (comanda DTR).
  const requested = useRef(false)
  useEffect(() => {
    if (step === 4 && task !== null && !requested.current) {
      requested.current = true
      void requestDeviceTree(device).catch(() => {})
    }
  }, [step, task, device])
  const askTree = () => { void requestDeviceTree(device).catch(() => {}); void tree.refetch() }
  // Asteapta stergerea INAINTE de onClose/refresh: altfel lista se reincarca
  // inaintea DELETE-ului si sarcina anulata reapare ca fila fantoma.
  const cancel = async () => { if (task !== null) await send(`/protectee/tasks/${task}/cancel`, {}); onClose() }
  const titles = ['Task name', 'Algorithm', 'Task settings', 'Task directory tree']
  return (
    <Modal id="addNewTask" open onClose={() => void cancel()} staticBackdrop keyboard={false} title={`New task - step ${step} of 4`}>
      <div className="panel panel-primary">
        <div className="panel-heading">{titles[step - 1]}</div>
        <div className="panel-body">
          {step === 1 ? (
            <form className="form-horizontal labelcustomize tk-action-form taskName" onSubmit={(e) => { e.preventDefault(); void send(`/protectee/devices/${device}/tasks`, { taskName: name }, (a) => { setTask(a.task ?? null); setStep(2) }) }}>
              <Field label="Name"><input className="form-control" name="taskName" value={name} onChange={(e) => setName(e.target.value)} required /></Field>
              <button type="submit" className="btn btn-sm btn-primary nextStep">Next</button>
            </form>
          ) : null}
          {step === 2 && task !== null ? <AlgorithmForm backup={backup} algorithm={{}} submitLabel="Next" onSubmit={(f) => void send(`/protectee/tasks/${task}/algorithm`, f, () => setStep(3))} /> : null}
          {step === 3 && task !== null ? <SettingsForm backup={backup} values={{ destination_id: 0, path: '', user: '', has_password: false, destination_key: '' }} submitLabel="Next" onSubmit={(f) => void send(`/protectee/tasks/${task}/settings`, f, () => setStep(4))} /> : null}
          {step === 4 && task !== null ? (tree.data ? <TreeForm tree={tree.data.tree} submitLabel="Save" onRefresh={askTree} onSubmit={(paths) => void send(`/protectee/tasks/${task}/tree`, { directory: paths }, onClose)} /> : <Spinner />) : null}
        </div>
      </div>
      <button type="button" className="btn btn-danger btn-sm cancelTask" onClick={() => void cancel()}>Cancel</button>
    </Modal>
  )
}

/** assignProfileModal.php: un profil ales din lista firmei; fara profile, "No profiles defined". */
function AssignProfile({ device, backup, open, onClose, onSaved }: { device: number; backup: ProtecteeBackup; open: boolean; onClose(): void; onSaved(): void }) {
  const [profile, setProfile] = useState('')
  const has = backup.company_profiles.length > 0
  const save = () => { if (profile) void send(`/protectee/profiles/${profile}/assign`, { device }, () => { setProfile(''); onSaved() }) }
  return (
    <Modal
      id={`assignProfileModal-${device}`}
      open={open}
      onClose={onClose}
      title="Assign Profile"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>Close</button>
          {has ? <button type="button" className="btn btn-primary saveDeviceProfile" disabled={!profile} onClick={save}>Save</button> : null}
        </>
      }
    >
      {has ? (
        <form className="form-horizontal labelcustomize tk-action-form assignProfile" onSubmit={(e) => { e.preventDefault(); save() }}>
          <Field label="Profile">
            <select className="form-control" name="profile" value={profile} onChange={(e) => setProfile(e.target.value)} required>
              <option value="">Select...</option>
              {backup.company_profiles.map((p) => <option key={p.ID} value={p.ID}>{p.name}</option>)}
            </select>
          </Field>
        </form>
      ) : (
        <div className="alert alert-danger"><strong>No profiles defined</strong></div>
      )}
    </Modal>
  )
}
